import { cn } from 'lib/helpers/utility';
import { ACCENT_MODES, getBrandColor, getBrandHex } from 'lib/constants/brandPalette';

import Badge from 'components/shared/Badge/Badge';
import Button from 'components/shared/Button/Button';
import Divider from 'components/shared/Divider/Divider';
import RadioButton from 'components/shared/RadioButton/RadioButton';

import ColorGrid from './ColorGrid';
import OptionCard from './OptionCard';
import ShadeSelector from './ShadeSelector';

const AccentDot = ({ hex }) =>
  hex ? (
    <span className="block h-[18px] w-[18px] rounded-full" style={{ backgroundColor: hex }} />
  ) : (
    <span className="block h-[18px] w-[18px] rounded-full border border-dashed border-neutral-400" />
  );

const AccentSelector = ({ draft, onChange }) => {
  const main = getBrandColor(draft.main_color);
  const recommended = getBrandColor(main.recommendedAccent);
  const isCustom = draft.accent_mode === ACCENT_MODES.CUSTOM;

  const selectRecommended = () => onChange({ accent_mode: ACCENT_MODES.RECOMMENDED });
  const selectCustom = () =>
    onChange({
      accent_mode: ACCENT_MODES.CUSTOM,
      accent_color: draft.accent_color || recommended.key,
      // Start from the shade the recommended accent was shown in, so the preview doesn't jump.
      accent_shade: draft.main_shade
    });

  return (
    <div className="flex flex-col gap-3">
      <OptionCard
        isSelected={!isCustom}
        onSelect={selectRecommended}
        className="gap-3 py-3 pl-4 pr-5"
        data-qa="brand-accent-recommended">
        <RadioButton
          name="brand-accent"
          value={ACCENT_MODES.RECOMMENDED}
          isSelected={!isCustom}
          onChange={selectRecommended}
          label=""
          wrapperClassName="!w-auto"
          tabIndex={-1}
          wrapperTabIndex={-1}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-1.5">
              <p className="text-base font-500 leading-6 text-neutral-900">{recommended.label}</p>
              <AccentDot hex={getBrandHex(recommended.key, draft.main_shade)} />
            </div>
            <Badge text="Recommended" size="small" color="primary" variant="lighter" />
          </div>
          <p className="text-sm leading-[22px] text-neutral-700">
            Goes well with {main.label} - calm, and easy to tell apart.
          </p>
        </div>
      </OptionCard>

      <OptionCard
        isSelected={isCustom}
        onSelect={isCustom ? undefined : selectCustom}
        className={cn('flex-col items-stretch gap-3 px-4 py-3', isCustom && 'cursor-default')}
        data-qa="brand-accent-custom">
        <div className="flex items-center gap-3">
          <RadioButton
            name="brand-accent"
            value={ACCENT_MODES.CUSTOM}
            isSelected={isCustom}
            onChange={selectCustom}
            label=""
            wrapperClassName="!w-auto"
            tabIndex={-1}
            wrapperTabIndex={-1}
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex items-center gap-1.5">
              <p className="text-base font-500 leading-6 text-neutral-900">Choose my own</p>
              <AccentDot
                hex={isCustom ? getBrandHex(draft.accent_color, draft.accent_shade) : null}
              />
            </div>
            <p className="text-sm leading-[22px] text-neutral-700">
              Any of our colors, in any shade.
            </p>
          </div>
        </div>

        {isCustom && (
          <>
            <ColorGrid
              value={draft.accent_color}
              shade={draft.accent_shade}
              onChange={(accent_color) => onChange({ accent_color })}
              dataQaPrefix="brand-accent-color"
            />
            <div className="flex flex-col gap-3">
              <p className="text-sm leading-[22px] text-neutral-800">Shade</p>
              <ShadeSelector
                colorKey={draft.accent_color}
                value={draft.accent_shade}
                onChange={(accent_shade) => onChange({ accent_shade })}
                dataQaPrefix="brand-accent-shade"
              />
            </div>
            <Divider />
            <div className="flex items-center justify-between">
              <p className="text-sm leading-[22px] text-neutral-700">Not happy?</p>
              <Button
                text="Reset to default"
                variant="link"
                size="small"
                onClick={selectRecommended}
                data-qa="brand-accent-reset"
              />
            </div>
          </>
        )}
      </OptionCard>
    </div>
  );
};

export default AccentSelector;
